import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface BaseDrawerProps {
  entity: any;
  entityType: "candidat" | "enseigne" | "ville" | "location";
  onClose: () => void;
}

const ENTITY_CONFIG = {
  candidat: {
    urlPath: "candidats",
    imageKey: "avatar_url",
  },
  enseigne: {
    urlPath: "enseignes",
    imageKey: "logo_url",
  },
  ville: {
    urlPath: "collectivites",
    imageKey: "logo_url",
  },
  location: {
    urlPath: "emplacements",
    imageKey: "logo_url",
  },
};

export function BaseDrawer({
  entity,
  entityType,
  onClose,
  children,
}: BaseDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsOpen(true);
    });

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeydown);
    document.body.classList.add("overflow-hidden");

    // Dispatch event to close any open dropdowns
    window.dispatchEvent(new CustomEvent("drawer:opened"));

    return () => {
      document.removeEventListener("keydown", handleKeydown);
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const config = ENTITY_CONFIG[entityType];
  const profileUrl = `/search/${config.urlPath}/${entity.id}?no_prev_next=true`;

  return createPortal(
    <div className="fixed inset-0 z-[100000000] overflow-hidden">
      <div
        className={`absolute inset-0 bg-slate-800/70 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      />
      <div
        className={`absolute right-0 top-0 bottom-0 w-full lg:w-[1280px] bg-white shadow-xl flex flex-col transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 text-slate-400 hover:text-slate-600 bg-white rounded-full p-2 shadow-md"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Turbo Frame Content */}
        <div className="flex-1 overflow-y-auto h-full">
          <turbo-frame
            id={`profile-drawer-${entity.id}`}
            src={profileUrl}
            loading="lazy"
            className="block h-full"
          >
            <div className="flex items-center justify-center h-full">
              <span className="relative inset-0 pl-2 mr-0 w-5 h-5 before:content-[''] before:box-border before:absolute before:top-1/2 before:left-1/2 before:w-8 before:h-8 before:-mt-4 before:-ml-4 before:rounded-full before:border-2 before:animate-spin before:border-gray-500/50 before:border-t-gray-500"></span>
            </div>
          </turbo-frame>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Utility functions for formatting
export const formatCurrency = (value?: number) => {
  if (!value || value === 0) return "Non renseigné";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(value);
};

export const formatNumber = (value?: number) => {
  if (!value || value === 0) return "Non renseigné";
  return new Intl.NumberFormat("fr-FR").format(value);
};

export const formatBoolean = (value?: boolean) => {
  if (value === true) return "Oui";
  if (value === false) return "Non";
  return "Non renseigné";
};
